<script>
    import {onDestroy} from "svelte";
    import {addFormModal} from '../Forms/ModalFormManager.svelte';
    import NotepadNotes from './NotepadNotes.svelte';
    import {showContextMenu} from './ContextMenu.svelte';
    import {confirm} from './Confirmation.svelte';
    import {notifier} from '@beyonk/svelte-notifications';

    export let assessment;

    import Fa from "svelte-fa";
    import {faMagnifyingGlass, faSquareUpRight} from "@fortawesome/free-solid-svg-icons";

    export let isPopup = false;
    export let visible = true;

    let searchText = "";
    let hidden = new Set();

    let selectedSection;
    let selectedPage;

    let selectedSectionId;
    let selectedPageId;

    let popupWindow;

    function addSection() {
        addFormModal({
            title: "Add Section",
            fields: [
                {
                    name: "name",
                    required: true,
                    label: "Section Name"
                }
            ]
        }).then(formData => {
            assessment.model.addNotepadSection(formData).then((section) => {
                selectedSectionId = section.id.split(":")[2];
            });
        })
    }

    function addPage() {
        addFormModal({
            title: "Add Page",
            fields: [
                {
                    name: "name",
                    required: true,
                    label: "Page Name"
                }
            ]
        }).then(formData => {
            assessment.model.addNotepadPage(selectedSection, formData).then((page) => {
                selectedPageId = page.id.split(":")[3];
            });
        })
    }

    function handlePageMenu(evt, page) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Edit Page",
                action: () => {
                    addFormModal({
                        title: "Edit Page",
                        fields: [
                            {name: "name", required: true, label: "Page Name"}
                        ],
                        data: page.details
                    }).then(formData => {
                        assessment.model.saveNotepadPage(formData).then((page) => {
                            selectedPageId = page.id.split(":")[3];
                        });
                    })
                }
            },
            {
                name: "Delete Page",
                action: () => {
                    confirm(`Are you sure you want to delete "${page.details.name}" and all associated notes? (This cannot be undone)`).then(() => {
                        deletePage(page).then(notifier.success("Page Deleted"));
                    }).catch(e => {});
                }
            }
        ])
    }

    function handleSectionMenu(evt, section) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Edit Section",
                action: () => {
                    addFormModal({
                        title: "Edit Section",
                        fields: [
                            {name: "name", required: true, label: "Section Name"}
                        ],
                        data: section.details
                    }).then(formData => {
                        assessment.model.saveNotepadSection(formData).then((section) => {
                            selectedSectionId = section.id.split(":")[2];
                        });
                    })
                }
            },
            {
                name: "Delete Section",
                action: () => {
                    confirm(`Are you sure you want to delete "${section.details.name}" and all associated pages and notes? (This cannot be undone)`).then(() => {
                        deleteSection(section).then(notifier.success("Section Deleted"));
                    }).catch(e => {});
                }
            }
        ])
    }

    function deleteSection(section) {
        if (selectedSection && section.details._id == selectedSection._id) {
            selectedPage = undefined;
            selectedSection = undefined;
        }

        let promises = [];

        for (let page of Object.values(section.pages)) {
            promises.push(deletePage(page));
        }

        promises.push(assessment.model.deleteNotepadSection(section.details))

        return Promise.all(promises);
    }

    function deletePage(page) {
        if (selectedPage && page.details._id == selectedPage._id) {
            selectedPage = undefined;
        }

        let promises = [];
        for (let note of page.notes) {
            promises.push(assessment.model.deleteNotepadNote(note));
        }

        promises.push(assessment.model.deleteNotepadPage(page.details));

        return Promise.all(promises);
    }

    function selectSection(section) {
        selectedSection = section.details;
        selectedPage = undefined;
    }

    function setSelection() {
        if (selectedSectionId) {
            selectedSection = assessment.notepad[selectedSectionId].details;
            if (selectedSection) {
                selectedSectionId = undefined;
            }
        }

        if (selectedPageId) {
            if (assessment.notepad[selectedSection.section_id].pages[selectedPageId]) {
                selectedPage = assessment.notepad[selectedSection.section_id].pages[selectedPageId].details;
                selectedPageId = undefined;
            }
        }
    }

    function popout() {
        let width = 800;
        let height = 600;

        let savedSize = window.localStorage.getItem("notepad_popup_size");
        if (savedSize) {
            try {
                savedSize = JSON.parse(savedSize);
                width = savedSize.width;
                height = savedSize.height;
            } catch (e) {
                width = 800;
                height = 600;
            }
        }

        if (width > window.screen.availWidth) {
            width = window.screen.availWidth;
        }

        if (height > window.screen.availHeight) {
            height = window.screen.availHeight;
        }

        popupWindow = window.open(`/#/notepadPopup/${assessment.model.assessment_id}`, "notepad_popup", `scrollbars=yes,width=${width},height=${height}`);
        visible = false;
    }

    async function filterNotes(text) {
        hidden = new Set();
        if (text == "") return

        let lowerText = text.toLowerCase();

        Object.values(assessment.notepad).forEach((section) => {
            let hide_section = true;
            Object.values(section.pages).forEach((page) => {
                let hide_page = true;
                page.notes.forEach(note => {
                    let hide_note = true;
                    if (note.note.toLowerCase().includes(lowerText)) {
                        hide_note = false;
                        hide_page = false;
                        hide_section = false;
                    }

                    if (hide_note) {
                        hidden.add(note.note_id);
                    }
                });

                if (hide_page) {
                    hidden.add(page.details.page_id);
                }
            });


            if (hide_section) {
                hidden.add(section.details.section_id);
            }
        });

        hidden = hidden;
        // selectedSection = undefined;
        selectedPage = undefined;
    }


    $: setSelection(assessment);
    $: filterNotes(searchText);

    onDestroy(() => {
        if (popupWindow) {
            popupWindow.close();
        }
    });
</script>

<div class="has-background-light p-2">
    <span class="title is-3">{assessment.details.name}</span>
    <div class="field">
        <p class="control has-icons-left has-icons-right">
            <input class="input is-rounded" type="text" placeholder="Search" bind:value={searchText}>
            <span class="icons">
                <span class="icon is-right">
                    <button class="delete" aria-label="delete" on:click={() => searchText = ""}></button>
                </span>
                <span class="icon is-small is-left magnifying-glass">
                    <Fa icon={faMagnifyingGlass} />
                </span>
            </span>
        </p>
    </div>
</div>
<div class="outer">
    <div class="sections">
        <div>
            {#if !isPopup}
                <span class="pointer is-pulled-right" on:click={popout}>Popout <Fa icon={faSquareUpRight} /></span>
            {/if}
        </div>
        <p class="heading">
            Sections
        </p>
        <ul>
            {#each Object.values(assessment.notepad) as section (section.details.section_id)}
                <li class:active={selectedSection && section.details._id == selectedSection._id} class:is-hidden={hidden.has(section.details.section_id)} on:click={() => selectSection(section)}
                    on:contextmenu|preventDefault={(evt) => handleSectionMenu(evt, section)}>
                    {section.details.name}
                </li>
            {/each}
        </ul>
        <button class="button is-link is-outlined is-fullwidth" on:click={addSection}>
            Add Section
        </button>
    </div>

    {#if selectedSection}
        <div class="pages">
            <p class="heading">
                Pages
            </p>
            <ul>
                {#each Object.values(assessment.notepad[selectedSection.section_id].pages) as page (page.details.page_id)}
                    <li class:active={selectedPage && page.details.page_id == selectedPage.page_id} class:is-hidden={hidden.has(page.details.page_id)} on:click={() => selectedPage = page.details}
                        on:contextmenu|preventDefault={(evt) => handlePageMenu(evt, page)}>
                        {page.details.name}
                    </li>
                {/each}
            </ul>
            <button class="button is-link is-outlined is-fullwidth" on:click={addPage}>
                Add Page
            </button>
        </div>
    {/if}

    {#if selectedSection && selectedPage}
        <div class="notes">
            <NotepadNotes {assessment} section={selectedSection} page={selectedPage} {hidden}></NotepadNotes>
        </div>
    {/if}
</div>


<style>
    .outer {
        display: flex;
        height: calc(100% - 5.5rem);
        width: 100%;
    }

    .sections, .pages {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 220px;
        padding: 5px;
        box-shadow: 0 .5em 1em -.125em rgba(10,10,10,.1),0 0 0 1px rgba(10,10,10,.02)
    }

    ul {
        flex-grow: 1;
        overflow: auto;
    }

    li {
        display: flex;
        cursor: pointer;
        padding: .5em;
        min-width: 175px;
    }

    li:hover {
        background-color: #f5f5f5;
    }

    li.active {
        background-color: hsl(171, 100%, 41%);
        color: white;
    }

    .button {
        margin-top: auto;
    }

    .notes{
        display: flex;
        flex-direction:column;
        flex-grow: 1;
        height: 100%;
        padding: .5em;
    }
</style>